<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.receipt.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button v-if="isEditModernTrade" size="md" type="neutral" @click="openCreate">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.receipt.create") }}
          </base-button>
          <base-button v-if="isEditModernTrade" size="md" type="secondary" @click="openSettings">
            <i class="fa fa-cog"></i>
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row">
            <div class="col-md-4">
              <base-input
                v-model="search"
                prepend-icon="fas fa-search"
                :placeholder="$t('modern_trade.receipt.po_number')"
                @keyup.enter.native="loadData"
              ></base-input>
            </div>
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="companyFilter"
                  :placeholder="$t('modern_trade.company.name')"
                  clearable
                  @change="loadData"
                  class="w-100"
                >
                  <el-option
                    v-for="c in companies"
                    :key="c.id"
                    :label="c.name"
                    :value="c.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="statusFilter"
                  :placeholder="$t('modern_trade.receipt.status')"
                  clearable
                  @change="loadData"
                  class="w-100"
                >
                  <el-option :label="$t('modern_trade.receipt.status_draft')" :value="1"></el-option>
                  <el-option :label="$t('modern_trade.receipt.status_received')" :value="2"></el-option>
                  <el-option :label="$t('modern_trade.receipt.status_canceled')" :value="9"></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-2 text-right">
              <base-button size="sm" type="primary" @click="loadData">
                <i class="fa fa-search"></i>
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table
            :data="rows"
            row-key="id"
            header-row-class-name="thead-light"
          >
            <el-table-column :label="$t('modern_trade.receipt.po_number')" min-width="140">
              <template slot-scope="{ row }">
                <a href="#" @click.prevent="goDetail(row.id)">{{ row.po_number }}</a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.company.name')" min-width="160">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.receipt.status')" min-width="100" align="center">
              <template slot-scope="{ row }">
                <span class="badge" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.receipt.items')" min-width="80" align="center">
              <template slot-scope="{ row }">{{ row.item_count || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('created_by') || 'Created By'" min-width="130">
              <template slot-scope="{ row }">{{ row.created_by_name || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.receipt.received_at')" min-width="140">
              <template slot-scope="{ row }">{{ formatDate(row.received_at) }}</template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="100" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="goDetail(row.id)">
                  <i class="fa fa-eye"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- Create Receipt Dialog -->
    <el-dialog
      :title="$t('modern_trade.receipt.create')"
      :visible.sync="showCreateDialog"
      width="560px"
      @closed="resetCreateForm"
    >
      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.name") }} *</label>
            <el-select v-model="createForm.company_id" class="w-100" filterable>
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.receipt.po_number") }} *</label>
            <input v-model="createForm.po_number" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.receipt.note") }}</label>
            <input v-model="createForm.note" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.receipt.po_file") }} * (PDF / JPG / PNG / XLSX, ≤ 10 MB)</label>
            <input
              ref="poFileInput"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx"
              class="form-control"
              @change="onPoFileChange"
            />
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showCreateDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="creating" @click="handleCreate">
          {{ $t("modern_trade.receipt.upload_po") }}
        </el-button>
      </span>
    </el-dialog>

    <!-- Settings Dialog (LOCKED Q10) -->
    <el-dialog
      :title="$t('modern_trade.settings.title')"
      :visible.sync="showSettingsDialog"
      width="480px"
    >
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.settings.min_shelf_life_days") }}</label>
        <input v-model.number="settingsForm.min_shelf_life_days" class="form-control" type="number" min="0" />
        <small class="text-muted">{{ $t("modern_trade.settings.min_shelf_life_hint") }}</small>
      </div>
      <!-- #512 — ช่องทางการขาย / วิธีชำระเงินที่ออเดอร์จากใบส่งของถูกสร้างด้วย
           เป็น dropdown จากรายการที่ BE ส่งมาเท่านั้น ไม่ใช่ช่องกรอกตัวเลข: id ที่ผิด
           หนึ่งตัวไม่ทำให้เกิด error ที่ไหนเลย แต่ทำให้ออเดอร์ทุกใบหลุดจากถังตัดรอบ
           ของห้องแพ็คแบบเงียบ ๆ (ช่องทางประเภทตัวแทนถูกกรองออกจากถัง) -->
      <!-- ไม่มี clearable: PUT ไม่มีทางล้างค่า (ส่งได้แค่ id ที่ใช้ได้) ถ้าปล่อยให้ล้างได้
           ผู้ใช้จะล้างแล้วกดบันทึก เห็นข้อความว่าสำเร็จ ทั้งที่ค่าเดิมยังอยู่เหมือนเดิม -->
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.settings.sale_channel") }}</label>
        <el-select
          v-model="settingsForm.sale_channel_id"
          class="w-100"
          filterable
          :placeholder="$t('modern_trade.settings.select_placeholder')"
        >
          <el-option
            v-for="o in saleChannelSelectOptions"
            :key="o.id"
            :label="o.name"
            :value="o.id"
            :disabled="o.retired"
          ></el-option>
        </el-select>
        <small class="text-muted">{{ $t("modern_trade.settings.sale_channel_hint") }}</small>
        <!-- "ตั้งไว้ที่ X แต่ใช้ไม่ได้" กับ "ยังไม่เคยตั้ง" ต้องแยกกันให้เห็น เพราะ
             ผู้ใช้ต้องทำคนละอย่าง และ dropdown ว่างเปล่าเฉย ๆ อ่านได้เป็นอย่างหลังเสมอ
             ส่วนข้อความของกรณี "ใช้ไม่ได้" มาจากรหัสเหตุผลที่ BE ส่งมา ไม่ได้เดาจากธง usable
             (deleted / missing / not_selectable ผู้ใช้ต้องทำคนละอย่างกันอีกชั้นหนึ่ง) -->
        <div v-if="saleChannelUnusable" class="alert alert-danger py-2 mt-2 mb-0 small">
          <i class="fa fa-exclamation-triangle"></i>
          {{ settingsIssueWarning("sale_channel") }}
        </div>
        <div v-else-if="saleChannelUnset" class="alert alert-warning py-2 mt-2 mb-0 small">
          <i class="fa fa-exclamation-circle"></i> {{ $t("modern_trade.settings.unset_warning") }}
        </div>
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.settings.payment_method") }}</label>
        <el-select
          v-model="settingsForm.payment_method_id"
          class="w-100"
          filterable
          :placeholder="$t('modern_trade.settings.select_placeholder')"
        >
          <el-option
            v-for="o in paymentMethodSelectOptions"
            :key="o.id"
            :label="o.name"
            :value="o.id"
            :disabled="o.retired"
          ></el-option>
        </el-select>
        <small class="text-muted">{{ $t("modern_trade.settings.payment_method_hint") }}</small>
        <div v-if="paymentMethodUnusable" class="alert alert-danger py-2 mt-2 mb-0 small">
          <i class="fa fa-exclamation-triangle"></i>
          {{ settingsIssueWarning("payment_method") }}
        </div>
        <div v-else-if="paymentMethodUnset" class="alert alert-warning py-2 mt-2 mb-0 small">
          <i class="fa fa-exclamation-circle"></i> {{ $t("modern_trade.settings.unset_warning") }}
        </div>
      </div>
      <!-- รายการตัวเลือกโหลดไม่สำเร็จ = dropdown ว่าง ซึ่งหน้าตาเหมือน "ไม่มีตัวเลือกให้เลือก"
           ต้องบอกว่าเป็นความผิดพลาดของการโหลด ไม่ใช่ความจริงของข้อมูล -->
      <div v-if="settingsOptionsFailed" class="alert alert-warning py-2 mb-3 small">
        <i class="fa fa-exclamation-triangle"></i> {{ $t("modern_trade.settings.options_failed") }}
      </div>
      <!-- ที่นี่เคยเป็นสวิตช์ "ตัดสต็อกคลังหลักเมื่อออกใบส่งของ" (bo-api #505 ถอดออกแล้ว
           ทั้งฝั่ง API: GET ไม่คืน key นี้ และ PUT ที่ส่งมาตอบ 400 setting_retired)
           ไม่ปล่อยให้ช่องหายไปเฉย ๆ เพราะคนที่เคยเห็นสวิตช์จะไม่รู้ว่ากติกาเปลี่ยนแล้ว
           และจะยังเข้าใจว่าออกใบส่งของแล้วระบบตัดสต็อกให้ -->
      <div class="alert alert-warning mb-0">
        <div><strong>{{ $t("modern_trade.settings.stock_rule_title") }}</strong></div>
        <ul class="mb-0 pl-3 small">
          <li>{{ $t("modern_trade.settings.stock_rule_do") }}</li>
          <li>{{ $t("modern_trade.settings.stock_rule_order") }}</li>
          <li>{{ $t("modern_trade.settings.stock_rule_manual") }}</li>
        </ul>
      </div>
      <span slot="footer">
        <el-button @click="showSettingsDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="savingSettings" @click="handleSaveSettings">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtReceipts",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // #512 — รายการที่แสดงใน dropdown = ตัวเลือกจาก BE บวก "ค่าที่ตั้งไว้ตอนนี้"
    // เมื่อค่านั้นไม่อยู่ในรายการแล้ว ถ้าไม่เติมเข้าไป el-select จะโชว์ตัวเลข id ดิบ
    // (หรือช่องว่าง) ซึ่งอ่านได้เป็น "ยังไม่เคยตั้งค่า" ทั้งที่ความจริงคือชี้ไปที่แถวที่หายไป
    saleChannelSelectOptions() {
      return this.buildSelectOptions(this.saleChannelOptions, "sale_channel");
    },
    paymentMethodSelectOptions() {
      return this.buildSelectOptions(this.paymentMethodOptions, "payment_method");
    },
    // "ตั้งไว้แล้วแต่ใช้ไม่ได้" ยังตัดสินจาก usable เหมือนเดิม (ธงเดียวที่ BE รับประกันว่า
    // ตรงกับสิ่งที่ปุ่มสร้างออเดอร์มองเห็นจริง) ส่วน *_issue เอาไว้เลือก "คำ" ที่จะพูดเท่านั้น
    saleChannelUnusable() {
      return this.settingsCurrent.sale_channel_id > 0 && !this.settingsCurrent.sale_channel_usable;
    },
    paymentMethodUnusable() {
      return this.settingsCurrent.payment_method_id > 0 && !this.settingsCurrent.payment_method_usable;
    },
    saleChannelUnset() {
      return !(this.settingsCurrent.sale_channel_id > 0);
    },
    paymentMethodUnset() {
      return !(this.settingsCurrent.payment_method_id > 0);
    },
  },
  data() {
    return {
      loading: false,
      creating: false,
      savingSettings: false,
      rows: [],
      companies: [],
      total: 0,
      page: 1,
      perPage: 20,
      search: "",
      companyFilter: null,
      statusFilter: null,
      showCreateDialog: false,
      showSettingsDialog: false,
      poFile: null,
      createForm: {
        company_id: null,
        po_number: "",
        note: "",
      },
      settingsForm: {
        min_shelf_life_days: 30,
        sale_channel_id: null,
        payment_method_id: null,
      },
      // #512 — ค่าที่ "บันทึกอยู่จริง" ตอนนี้ แยกจาก settingsForm ที่เป็นค่าที่ผู้ใช้กำลังแก้
      // ต้องเก็บ id, ชื่อ, ธง usable และรหัสเหตุผล: id > 0 กับ usable=false = ตั้งไว้แต่ใช้ไม่ได้
      // ส่วน id = 0 = ยังไม่เคยตั้งค่า สองอย่างนี้ผู้ใช้ต้องทำคนละอย่าง
      // และ *_issue แยก "ใช้ไม่ได้" ออกเป็นสามเหตุผลที่ผู้ใช้ต้องทำคนละอย่างกันอีกชั้น
      settingsCurrent: {
        sale_channel_id: 0,
        sale_channel_name: "",
        sale_channel_usable: false,
        sale_channel_issue: "",
        payment_method_id: 0,
        payment_method_name: "",
        payment_method_usable: false,
        payment_method_issue: "",
      },
      saleChannelOptions: [],
      paymentMethodOptions: [],
      settingsOptionsFailed: false,
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.search) params.search = this.search;
        if (this.companyFilter) params.company_id = this.companyFilter;
        if (this.statusFilter) params.status = this.statusFilter;
        const res = await ModernTradeService.receiptsList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.resetCreateForm();
      this.showCreateDialog = true;
    },
    resetCreateForm() {
      this.createForm = { company_id: null, po_number: "", note: "" };
      this.poFile = null;
      if (this.$refs.poFileInput) this.$refs.poFileInput.value = "";
    },
    onPoFileChange(e) {
      this.poFile = e.target.files[0] || null;
    },
    async handleCreate() {
      if (!this.createForm.company_id) {
        swal.fire({ title: this.$t("modern_trade.receipt.company_required"), icon: "warning" });
        return;
      }
      if (!this.createForm.po_number) {
        swal.fire({ title: this.$t("modern_trade.receipt.po_number_required"), icon: "warning" });
        return;
      }
      if (!this.poFile) {
        swal.fire({ title: this.$t("modern_trade.receipt.po_file_required"), icon: "warning" });
        return;
      }
      this.creating = true;
      try {
        const fd = new FormData();
        fd.append("company_id", this.createForm.company_id);
        fd.append("po_number", this.createForm.po_number);
        if (this.createForm.note) fd.append("note", this.createForm.note);
        fd.append("file", this.poFile);
        const res = await ModernTradeService.receiptCreate(fd);
        const receipt = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.showCreateDialog = false;
        this.loadData();
        if (receipt.id) {
          this.$router.push(`/modern-trade/receipts/${receipt.id}`);
        }
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.creating = false;
      }
    },
    async openSettings() {
      // ทั้งสองเส้นอยู่ใต้ /api/v3/modern-trade/ (mw.IsModernTrade) — ห้ามไปเรียก
      // /sale-channels หรือ /payment-methods ของโมดูลอื่นที่กั้น SuperAdmin เด็ดขาด
      // ประตูพวกนั้นตอบ 403 access_denied ⇒ http.js เด้งหน้าต่าง "ไม่มีสิทธิ์" ทับหน้านี้
      // (ก่อน bo-api #937 ตอบ 401 ซึ่ง http.js อ่านว่า token หมดอายุ แล้วลบคุกกี้เด้งไป
      // /login ⇒ เจ้าหน้าที่ MT ถูก logout ทันทีที่กดปุ่มตั้งค่า — เคยเกิดจริงใน #236)
      await Promise.all([this.loadSettings(), this.loadSettingsOptions()]);
      this.showSettingsDialog = true;
    },
    async loadSettings() {
      try {
        const res = await ModernTradeService.settingsGet();
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.settingsForm.min_shelf_life_days = d.min_shelf_life_days !== undefined ? d.min_shelf_life_days : 30;
        this.settingsCurrent = {
          sale_channel_id: Number(d.sale_channel_id || 0),
          sale_channel_name: d.sale_channel_name || "",
          sale_channel_usable: !!d.sale_channel_usable,
          sale_channel_issue: typeof d.sale_channel_issue === "string" ? d.sale_channel_issue : "",
          payment_method_id: Number(d.payment_method_id || 0),
          payment_method_name: d.payment_method_name || "",
          payment_method_usable: !!d.payment_method_usable,
          payment_method_issue: typeof d.payment_method_issue === "string" ? d.payment_method_issue : "",
        };
        // ค่าเริ่มต้นของ dropdown = ค่าที่บันทึกอยู่ (รวมค่าที่ถูกลบ — ให้เห็นว่าตั้งอะไรไว้
        // ตัวเลือกนั้นถูกทำเป็น disabled ใน buildSelectOptions() จึงเลือกซ้ำไม่ได้)
        this.settingsForm.sale_channel_id = this.settingsCurrent.sale_channel_id || null;
        this.settingsForm.payment_method_id = this.settingsCurrent.payment_method_id || null;
      } catch (e) {
        this.settingsForm.min_shelf_life_days = 30;
        this.settingsCurrent = {
          sale_channel_id: 0,
          sale_channel_name: "",
          sale_channel_usable: false,
          sale_channel_issue: "",
          payment_method_id: 0,
          payment_method_name: "",
          payment_method_usable: false,
          payment_method_issue: "",
        };
        this.settingsForm.sale_channel_id = null;
        this.settingsForm.payment_method_id = null;
      }
    },
    async loadSettingsOptions() {
      try {
        const res = await ModernTradeService.settingsOptions();
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.saleChannelOptions = d.sale_channels || [];
        this.paymentMethodOptions = d.payment_methods || [];
        this.settingsOptionsFailed = false;
      } catch (e) {
        this.saleChannelOptions = [];
        this.paymentMethodOptions = [];
        this.settingsOptionsFailed = true;
      }
    },
    // prefix = "sale_channel" | "payment_method"
    buildSelectOptions(options, prefix) {
      const out = (options || [])
        .filter(o => o && Number(o.id) > 0)
        .map(o => ({ id: Number(o.id), name: o.name || `#${o.id}`, retired: false }));
      const curId = Number(this.settingsCurrent[`${prefix}_id`] || 0);
      if (curId > 0 && !out.some(o => o.id === curId)) {
        const usable = !!this.settingsCurrent[`${prefix}_usable`];
        const name = this.settingsCurrent[`${prefix}_name`] || `#${curId}`;
        out.unshift({
          id: curId,
          // ป้ายกำกับต้องบอกเหตุผลจริง ไม่ใช่ "(ถูกลบไปแล้ว)" เหมือนกันหมด — ค่าที่เป็น
          // ช่องทางประเภทตัวแทนไม่ได้ถูกลบ แถวยังอยู่ครบ ผู้ใช้ที่อ่านว่าถูกลบจะไปตามหาคนลบ
          name: usable
            ? name
            : this.$t(`modern_trade.settings.issue.${this.settingsIssueKind(prefix)}_option`, { name }),
          retired: !usable,
        });
      }
      return out;
    },
    settingsCurrentLabel(prefix) {
      const id = Number(this.settingsCurrent[`${prefix}_id`] || 0);
      return this.settingsCurrent[`${prefix}_name`] || (id > 0 ? `#${id}` : "-");
    },
    // #512 — GET ส่งรหัสเหตุผลมาให้แล้ว (MT-Setting.go: not_set / missing / deleted /
    // not_selectable / "" เมื่อใช้ได้) ทั้งสามเหตุผลของ "ใช้ไม่ได้" ผู้ใช้ต้องทำคนละอย่าง:
    //   deleted / missing  → แถวหายไปแล้ว ต้องเลือกค่าอื่นแทน
    //   not_selectable     → แถวยังอยู่ดี ๆ ไม่มีใครลบ แต่เป็นช่องทางประเภทตัวแทน ซึ่งถัง
    //                        ตัดรอบของห้องแพ็คกรองออก ⇒ ออเดอร์จะค้าง 111 เงียบ ๆ
    // ยุบทั้งสามเป็น "ถูกลบไปแล้ว" = บอกสาเหตุผิดในเคสที่อันตรายที่สุด
    settingsIssueKind(prefix) {
      const issue = this.settingsCurrent[`${prefix}_issue`];
      switch (typeof issue === "string" ? issue : "") {
        case "deleted":
          return "deleted";
        case "missing":
          return "missing";
        // not_selectable เกิดได้เฉพาะช่องทางการขาย (bo_payment_method ไม่มีแกนประเภท)
        // ถ้ามันโผล่มากับวิธีชำระเงิน แปลว่ากติกาฝั่ง BE เปลี่ยน ⇒ พูดกลาง ๆ ดีกว่าพูดผิด
        case "not_selectable":
          return prefix === "sale_channel" ? "not_selectable" : "unusable";
        // "" / not_set / รหัสใหม่ที่ FE ยังไม่รู้จัก → ข้อความกลางที่ยังบอกว่าต้องเลือกใหม่
        // (ไม่ปล่อยให้ $t คืน key ดิบ และไม่เงียบ)
        default:
          return "unusable";
      }
    },
    settingsIssueWarning(prefix) {
      return this.$t(`modern_trade.settings.issue.${this.settingsIssueKind(prefix)}_warning`, {
        name: this.settingsCurrentLabel(prefix),
      });
    },
    async handleSaveSettings() {
      // ช่องว่าง (ผู้ใช้ลบตัวเลขทิ้ง) กับค่าที่ไม่ใช่จำนวนเต็ม >= 0 ต้องถูกจับที่นี่
      // v-model.number คืนสตริงว่างเดิมเมื่อ parse ไม่ได้ ⇒ payload จะเป็น
      // {"min_shelf_life_days": ""} ซึ่ง BE unmarshal ลง *int ไม่ได้ แล้วตอบ
      // "invalid body" 400 ที่ไม่ได้บอกอะไรผู้ใช้เลยว่าต้องแก้ตรงไหน
      const days = this.settingsForm.min_shelf_life_days;
      if (days === "" || days === null || days === undefined || !Number.isInteger(Number(days)) || Number(days) < 0) {
        swal.fire({ title: this.$t("modern_trade.settings.min_shelf_life_invalid"), icon: "warning" });
        return;
      }
      this.savingSettings = true;
      try {
        // ห้ามแนบ deduct_main_stock กลับมาไม่ว่าค่าอะไร: BE ตอบ 400 setting_retired
        // และ return ก่อนแตะ min_shelf_life_days ⇒ แนบไปเมื่อไหร่ = บันทึกไม่ได้เลย
        const payload = { min_shelf_life_days: Number(days) };
        // แนบ id เฉพาะตอนที่ผู้ใช้เปลี่ยนค่าจริง ๆ เท่านั้น (#512)
        // เหตุผลเดียวกับ deduct_main_stock: id ที่ BE ปฏิเสธทำให้ PUT ทั้งก้อนตก
        // ⇒ ถ้าส่งค่าที่ตั้งอยู่เดิมกลับไปด้วยทุกครั้ง ระบบที่ช่องทางการขายถูกลบไปแล้ว
        // จะแก้ "อายุขั้นต่ำที่รับได้" ไม่ได้อีกเลย ทั้งที่เป็นคนละเรื่องกัน
        const sc = Number(this.settingsForm.sale_channel_id || 0);
        if (sc > 0 && sc !== this.settingsCurrent.sale_channel_id) payload.sale_channel_id = sc;
        const pm = Number(this.settingsForm.payment_method_id || 0);
        if (pm > 0 && pm !== this.settingsCurrent.payment_method_id) payload.payment_method_id = pm;
        await ModernTradeService.settingsPut(payload);
        swal.fire({ title: this.$t("modern_trade.settings.saved"), icon: "success", timer: 1500, showConfirmButton: false });
        // อ่านค่าที่บันทึกแล้วกลับมา: ธง usable และชื่อที่โชว์ต้องมาจาก BE เสมอ
        // ไม่ใช่เดาเอาจากสิ่งที่เพิ่งส่งไป
        await this.loadSettings();
        this.showSettingsDialog = false;
      } catch (err) {
        // 403 ถูก interceptor (src/resource/http.js:54-56) ยิง swal "ไม่มีสิทธิ์" ไปแล้ว
        // ยิงซ้ำที่นี่ = กล่องที่สองเด้งทับกล่องแรกทันที ผู้ใช้เห็นข้อความสุดท้ายที่กว้าง
        // กว่าและอ่านไม่ออกว่าเรื่องสิทธิ์ ⇒ ปล่อยให้ interceptor พูดคนเดียว
        const status = (err && err.response && err.response.status) || 0;
        if (status !== 403) swal.fire({ title: this.settingsErrMsg(err), icon: "error" });
      } finally {
        this.savingSettings = false;
      }
    },
    // ข้อความของ BE มาก่อนเสมอ: Cause คือที่เดียวที่รู้ว่า id ไหนถูกปฏิเสธเพราะอะไร
    // คำแปลตาม Msg เป็นแค่ตัวสำรองเผื่อ Cause ว่าง (invalid_sale_channel /
    // sale_channel_not_selectable / invalid_payment_method / setting_retired)
    settingsErrMsg(err) {
      const data = (err && err.response && err.response.data) || {};
      if (data.Cause) return data.Cause;
      const code = data.Msg || "";
      if (code) {
        const key = `modern_trade.settings.error.${code}`;
        const label = this.$t(key);
        if (label !== key) return label;
        return code;
      }
      return (err && err.message) || "";
    },
    goDetail(id) {
      this.$router.push(`/modern-trade/receipts/${id}`);
    },
    statusLabel(status) {
      switch (status) {
        case 1: return this.$t("modern_trade.receipt.status_draft");
        case 2: return this.$t("modern_trade.receipt.status_received");
        case 9: return this.$t("modern_trade.receipt.status_canceled");
        default: return "-";
      }
    },
    statusClass(status) {
      switch (status) {
        case 1: return "badge-warning";
        case 2: return "badge-success";
        case 9: return "badge-secondary";
        default: return "badge-secondary";
      }
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    },
  },
};
</script>
